import {
  EconomicCalendarEvent as ApiEvent,
  EventPotency,
  EventVolatility,
} from '@/types/generated/public_api'

// ─── Types ─────────────────────────────────────────────────────────────────────

export type Impact = 'high' | 'medium' | 'low'

export interface CalendarEvent {
  id: string
  dateUtc: number
  title: string
  time: string
  country: string
  currency: string
  impact: Impact
  unit?: string
  actual?: number
  consensus?: number
  previous?: number
  isBetterThanExpected?: boolean
  potency?: EventPotency
}

// ─── Mappers ───────────────────────────────────────────────────────────────────

export function volatilityToImpact(v?: EventVolatility): Impact {
  if (v === EventVolatility.EVENT_VOLATILITY_HIGH) return 'high'
  if (v === EventVolatility.EVENT_VOLATILITY_MEDIUM) return 'medium'
  return 'low'
}

export function formatEventTime(
  dateUtc: string | undefined,
  isAllDay: boolean,
  locale?: string,
  timeZone?: string
): string {
  if (isAllDay) return 'All Day'
  const ms = Number(dateUtc)
  if (!ms) return '-'
  return new Intl.DateTimeFormat(locale, {
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
    ...(timeZone ? { timeZone } : {}),
  }).format(new Date(ms))
}

export function getLocalCalendarDayRange(date: Date) {
  const year = date.getFullYear()
  const month = date.getMonth()
  const day = date.getDate()
  return {
    fromMs: new Date(year, month, day, 0, 0, 0, 0).getTime(),
    toMs: new Date(year, month, day, 23, 59, 59, 999).getTime(),
  }
}

export function isSameLocalCalendarDate(eventTimestampMs: number, calendarDate: Date) {
  const eventDate = new Date(eventTimestampMs)
  return (
    eventDate.getFullYear() === calendarDate.getFullYear() &&
    eventDate.getMonth() === calendarDate.getMonth() &&
    eventDate.getDate() === calendarDate.getDate()
  )
}

export function formatValue(
  value: number | undefined,
  unit?: string,
  potency?: EventPotency
): string {
  if (value === undefined || value === null) return '-'
  const suffix =
    potency === EventPotency.EVENT_POTENCY_K
      ? 'K'
      : potency === EventPotency.EVENT_POTENCY_M
        ? 'M'
        : potency === EventPotency.EVENT_POTENCY_B
          ? 'B'
          : potency === EventPotency.EVENT_POTENCY_T
            ? 'T'
            : ''
  const unitStr = unit ?? ''
  // unit goes AFTER the number (e.g. "5.2%" not "%5.2")
  return `${value}${suffix}${unitStr}`
}

export function apiEventToCalendar(e: ApiEvent): CalendarEvent {
  return {
    id: e.eventDateId,
    dateUtc: parseInt(e.dateUtc, 10),
    title: e.name,
    time: formatEventTime(e.dateUtc, e.isAllDay),
    country: e.countryCode,
    currency: e.currencyCode,
    impact: volatilityToImpact(e.volatility),
    unit: e.unit,
    actual: e.actual,
    consensus: e.consensus,
    previous: e.previous,
    isBetterThanExpected: e.isBetterThanExpected,
    potency: e.potency,
  }
}

// ─── Country flags ─────────────────────────────────────────────────────────────

const COUNTRY_ALIASES: Record<string, string> = {
  UK: 'GB',
  EZ: 'EU',
  XK: 'XK',
}

export function countryToFlag(code: string): string {
  if (!code || code.length !== 2) return ''
  const normalized = COUNTRY_ALIASES[code.toUpperCase()] ?? code.toUpperCase()
  return normalized.replace(/./g, (char) => String.fromCodePoint(char.charCodeAt(0) + 127397))
}

// ─── EE-type chip preview model ─────────────────────────────────────────────────

export interface EeTypePreviewModel {
  /** Nearest future event, else nearest past event — drives the popover header. */
  headerEvent: CalendarEvent
  /** Nearest future event with a consensus figure set, if any. */
  nextRelease: CalendarEvent | null
  /** Past events with an actual figure set, ASC by date, capped to the most recent 100. */
  history: CalendarEvent[]
}

/**
 * Builds the eeType chip preview model from a raw event history response.
 * Pure — `now` is injectable so tests aren't wall-clock-dependent, and the
 * server's response order is never assumed (always sorted here).
 */
export function buildEeTypePreviewModel(
  events: ApiEvent[],
  now: number = Date.now()
): EeTypePreviewModel | null {
  const sorted = events.map(apiEventToCalendar).toSorted((a, b) => a.dateUtc - b.dateUtc)

  const past = sorted.filter((e) => e.dateUtc <= now)
  const future = sorted.filter((e) => e.dateUtc > now)

  const nextRelease = future.find((e) => e.consensus != null) ?? null
  const history = past.filter((e) => e.actual != null).slice(-100)

  const headerEvent = nextRelease ?? history[history.length - 1] ?? null
  if (!headerEvent) return null

  return { headerEvent, nextRelease, history }
}
